/**
 * Simulation Helpers
 *
 * Utilities for transforming simulation engine results into formats
 * expected by UI components like JourneyReveal.
 */

import { PORTFOLIO_DEFAULTS } from '../config/portfolioDefaults';

/**
 * Generate projections array from engine's freedomCurve
 * JourneyReveal expects MONTHLY projections (12 entries per year)
 * It samples every 12th entry to build yearly snapshots
 *
 * @param {Object} freedomCurve - Freedom curve from modelAcquisitionTimeline result
 * @param {number} maxYears - Maximum years to generate (default: 20)
 * @returns {Array} Monthly projections array for JourneyReveal
 */
export const generateProjectionsFromFreedomCurve = (freedomCurve, maxYears = 20) => {
  // Engine returns freedomCurve.curve (array of yearly data points)
  // Each point has: yearsAhead, targetYear, passiveIncome, strategy, etc.
  if (!freedomCurve?.curve || !Array.isArray(freedomCurve.curve)) {
    return [];
  }

  const projections = [];
  const curveData = freedomCurve.curve.slice(0, maxYears);

  // Generate monthly projections (JourneyReveal samples every 12th entry)
  curveData.forEach((curvePoint, yearIndex) => {
    const year = curvePoint.yearsAhead || yearIndex + 1;
    const phase = determinePhase(year, curveData.length);
    
    // Extract data from curve point's strategy result
    const strategyResult = curvePoint.strategy?.result || {};
    const totals = strategyResult.totals || {};
    
    // Properties count from keep count (remaining after sells)
    const propertyCount = curvePoint.strategy?.keepCount || 0;
    const totalLoans = totals.remainingLoans || 0;
    const totalValue = totals.unencumberedValue || 0;

    // Create 12 monthly entries for this year
    for (let month = 1; month <= 12; month++) {
      const absoluteMonth = yearIndex * 12 + month;

      projections.push({
        // Core fields JourneyReveal expects
        month: absoluteMonth,
        year,
        totalValue: totalValue,
        totalDebt: totalLoans,
        totalEquity: totalValue - totalLoans,
        usableEquity: (totalValue - totalLoans) * 0.8,
        portfolioLvr: totalValue > 0 ? totalLoans / totalValue : 0,
        lvr: totalValue > 0 ? totalLoans / totalValue : 0,
        netCashFlow: (curvePoint.passiveIncome || 0) / 12, // Monthly passive income
        phase,
        events: [],

        // Property tracking (simplified - JourneyReveal uses this for counts)
        properties: Array.from({ length: propertyCount }, (_, i) => ({
          id: `prop-${year}-${i}`,
          soldMonth: null,
          loanBalance: propertyCount > 0 ? totalLoans / propertyCount : 0,
          isDebtFree: curvePoint.feasible || false,
        })),

        // Additional fields for display
        passiveIncome: curvePoint.passiveIncome || 0,
        propertyCount: propertyCount,
      });
    }
  });

  return projections;
};

/**
 * Determine journey phase based on year progress
 *
 * @param {number} year - Current year (1-indexed)
 * @param {number} maxYears - Total years in journey
 * @returns {string} Phase name: BUILD, OPTIMIZE, HARVEST, or FREEDOM
 */
const determinePhase = (year, maxYears) => {
  const progress = year / maxYears;
  if (progress < 0.6) return 'BUILD';
  if (progress < 0.85) return 'OPTIMIZE';
  if (progress < 1) return 'HARVEST';
  return 'FREEDOM';
};

/**
 * Normalize investment goal with all required fields
 * Ensures consistent data structure across the app
 *
 * @param {Object} goal - Raw goal object from Strategy or context
 * @returns {Object} Normalized goal with all required fields
 */
export const normalizeInvestmentGoal = (goal) => {
  if (!goal) return null;

  return {
    // PRESERVE the caller's goal first. This function used to be a
    // non-spreading whitelist: FreedomJourney's lock-in fields
    // (initialCapital, networthTarget, passiveIncome, achievedNetworth,
    // timelineEvents, portfolio, optimalStrategy, confidenceLevel,
    // scenarioAnalysis, portfolioSnapshot, completedAt) were all silently
    // discarded here, and the "defaults" below fabricated replacements —
    // so every Portfolio surface recomputed with $100k capital / $150k
    // income and disagreed with the Strategy reveal the user just saw.
    ...goal,

    // Goal identification
    goalType: goal.goalType || 'passive_income',

    // Core metrics - required for display.
    // targetPassiveIncome is QuickGoalStep's field name. ?? (not ||) so a
    // PPOR goal's explicit targetIncome: 0 survives instead of becoming
    // the $80k default.
    targetIncome: goal.targetIncome ?? goal.targetPassiveIncome ?? PORTFOLIO_DEFAULTS.targetIncome,
    yearsToFreedom: goal.yearsToFreedom || goal.timeline || PORTFOLIO_DEFAULTS.timelineYears,
    propertiesNeeded: goal.propertiesNeeded || goal.strategyDetails?.propertiesNeeded || 0,
    confidence: goal.confidence || PORTFOLIO_DEFAULTS.confidenceDefault,

    // PPOR-specific
    pporBalance: goal.pporBalance || 0,

    // Financial inputs. FreedomJourney saves the user's capital as
    // `initialCapital` — map it across instead of fabricating the default.
    // No fabricated householdIncome: consumers must read the real value
    // from investorProfile (a hardcoded 150000 here drove ScenariosTab's
    // tax brackets off invented income).
    startingCapital: goal.startingCapital ?? goal.initialCapital ?? PORTFOLIO_DEFAULTS.startingCapital,
    householdIncome: goal.householdIncome ?? null,

    // Simulation results (for JourneyReveal)
    simulation: goal.simulation || null,
    projections: goal.projections || null,

    // Strategy details (legacy compatibility)
    strategyDetails: goal.strategyDetails || null,

    // Timestamps
    createdAt: goal.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Extract key metrics from simulation result for display
 *
 * @param {Object} simulationResult - Result from modelAcquisitionTimeline
 * @returns {Object} Extracted metrics for display
 */
export const extractSimulationMetrics = (simulationResult) => {
  if (!simulationResult) return null;

  const { summary, freedomYear, goalAchievable, optimalStrategy } = simulationResult;

  return {
    yearsToFreedom: summary?.yearsToGoal || (freedomYear ? freedomYear - new Date().getFullYear() : null),
    propertiesNeeded: summary?.totalPropertiesNeeded || summary?.propertiesAcquired || 0,
    confidence: summary?.confidence || PORTFOLIO_DEFAULTS.confidenceDefault,
    goalAchievable: goalAchievable || false,
    achievablePassiveIncome: summary?.achievablePassiveIncome || 0,
    achievableNetworth: summary?.achievableNetworth || 0,
    finalDebtFreeCount: optimalStrategy?.keepCount || summary?.totalPropertiesNeeded || 0,
    harvestYear: calculateHarvestYear(freedomYear, summary?.yearsToGoal),
  };
};

/**
 * Calculate harvest year (85% of journey)
 */
const calculateHarvestYear = (freedomYear, yearsToGoal) => {
  const currentYear = new Date().getFullYear();

  if (freedomYear) {
    const totalYears = freedomYear - currentYear;
    return Math.floor(totalYears * 0.85) + currentYear;
  }

  if (yearsToGoal) {
    return Math.floor(yearsToGoal * 0.85);
  }

  return null;
};

export default {
  generateProjectionsFromFreedomCurve,
  normalizeInvestmentGoal,
  extractSimulationMetrics,
};
